import { Component, Prop, Element, Host, Build, h } from '@stencil/core';

/**
 * @description  When wrapped around a section of HTML, the HTML will be styled according to the Design
 * System Guidelines and theme.
 * @use
 * @masked
 * @since 4.0.0
 * @ssh
 * @version figma-v1
 * @slot - `default slot`: The text to be styled.
 */
@Component({
  tag: 'hp6-semantic',
  styleUrl: 'semantic.scss',
  shadow: false, // Already satisfies the single-tag policy without shadow DOM
})
export class Semantic {
  /**
   * Content design size to use throughout this instance.
   */
  @Prop({ reflect: true }) sizeSelection: 'xxs' | 'xs' | 'sm' | 'md' | 'lg' | 'xl' = 'md';

  /**
   * Flag to set no bullets or list discs for this instance.
   */
  @Prop() noBullets: boolean = false;

  /**
   * Boolean to set the semantic text to inverted (white).
   */
  @Prop() isInverted: boolean = false;

  /**
   * Boolean to wrap tables in hp6-table. Off by default.
   */
  @Prop() wrapTables: boolean = false;

  @Element() host: HTMLHp6SemanticElement;

  /**
   * Wraps each child link found in the provided host
   * with a hp6-link-text-basic tag.
   *
   * @param host - The element to search for links.
   * @param isInverted - Whether the link should be inverted
   * @param newWindowText - Text to append to the aria-label if the link opens in a new window
   */
  static wrapLinks(
    host: HTMLElement,
    isInverted: boolean = false,
    newWindowText: string = undefined,
  ) {
    const links: NodeListOf<HTMLAnchorElement> = host.querySelectorAll('a');
    const hpLinks = [];
    links.forEach(link => {
      const parentTagName = link.parentElement.tagName.toLocaleLowerCase();
      // only replace links that are not already inside link-text-basic
      if (parentTagName !== 'hp6-link-text-basic') {
        const hpLink = document.createElement('hp6-link-text-basic');
        // set props of new tag
        if (isInverted === true) {
          hpLink.isInverted = true;
        }
        if (newWindowText && link.getAttribute('target') === '_blank') {
          hpLink.setAttribute('new-window-text', newWindowText);
        }
        link.parentNode.replaceChild(hpLink, link);
        hpLink.appendChild(link);
        hpLinks.push(hpLink);
      }
    });
    return hpLinks;
  }

  static wrapTables(host: HTMLElement) {
    const tables = host.querySelectorAll('table');
    tables.forEach(table => {
      if (table.parentElement.tagName.toLocaleLowerCase() !== 'hp6-table') {
        const hp6Table = document.createElement('hp6-table');
        table.parentElement.replaceChild(hp6Table, table);
        hp6Table.appendChild(table);
      }
    });
  }

  wrapElements() {
    Semantic.wrapLinks(this.host, this.isInverted);
    if (this.wrapTables) {
      Semantic.wrapTables(this.host);
    }
  }

  componentWillLoad() {
    this.wrapElements();

    // MutationObserver only works in browser, not server-side rendering
    if (Build.isBrowser) {
      // Use MutationObserver to watch for changes to the DOM
      const observer = new MutationObserver(() => {
        this.wrapElements();
      });

      observer.observe(this.host, {
        childList: true,
        subtree: true,
      });
    }
  }

  render() {
    const { sizeSelection, noBullets, isInverted } = this;
    return (
      <Host
        class={`hp6-semantic-styles ${sizeSelection}${noBullets ? ' no-bullets' : ''}
         ${isInverted ? ' inverted' : ''}`}
      >
        <slot />
      </Host>
    );
  }
}
